/**
 * @Description Fasat Filet Input
 * @FileName fasatFilterInput.js
 * @Author Abhisek Kundu - kundabh
 * @CreatedOn 24 June, 2021 21:40:00
 * @IssueID - 1050
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { globalConstants, staticCommonLabelKeys } from '../../../moduleConstants';
import FasatActionableTextInput from '../../fasatActionableTextInput/fasatActionableTextInput';
import FasatDatePicker from '../../fasatDatePicker/fasatDatePicker';
import FasatDropdown from '../../fasatDropdown/fasatDropdown';
import FasatTextInput from '../../fasatTextInput/fasatTextInput';
import FasatFormErrors from '../../fasatFormErrors/fasatFormErrors';
import { getCurrentLocale } from '../../../i18n';
import { getDateFormat, dateFieldValidator } from '../../../util';

const OPT_IS_NULL_ID = 13;
const OPT_IS_NOT_NULL_ID = 14;
const OPT_IS_EMPTY_ID = 20;
const OPT_IS_NOT_EMPTY_ID = 21;
const { dateFormatMMDDYYYY } = globalConstants;

const generateInputComponent = (isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId) => (
  <FasatTextInput
    disabled={isFieldDisable}
    onChange={changeInputValue}
    value={values}
    errors={showRequired}
    aria-label={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
    onInputEnabled={type === 'number'}
    inputNum={type === 'number'}
    data-testid={dataTestId}
  />
);

/**
* Return Fasat Actionable Text Input Component
* @returns Component
*/
const grtFasatActionTxtInp = (isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId) => (
  <FasatActionableTextInput
    disabled={isFieldDisable}
    changeValues={changeInputValue}
    type={type}
    inlineButtonText={t(staticCommonLabelKeys.ARIA_COMMON_ACTION_BUTTON_ADD)}
    value={values || null}
    errors={showRequired}
    ariaLabel={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
    data-testid={dataTestId}
  />
);

/**
* Return Fasat Text Range Input Component
* @returns Component
*/
const generateFasatTextInputRange = ({ isFieldDisable, type, t, values, handleChangeRangeFrom, handleChangeTo, dataTestId }) => (
  <div clasName="inputRange">
    <FasatTextInput
      value={values ? values[0] : null}
      disabled={isFieldDisable}
      onChange={handleChangeRangeFrom}
      aria-label={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
      onInputEnabled={type === 'number'}
      inputNum={type === 'number'}
      data-testid={dataTestId ? `${dataTestId}-from` : undefined}
    />
    <span className="hyphen">-</span>
    <FasatTextInput
      value={values ? values[1] : null}
      disabled={isFieldDisable}
      onChange={handleChangeTo}
      aria-label={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
      onInputEnabled={type === 'number'}
      inputNum={type === 'number'}
      data-testid={dataTestId ? `${dataTestId}-to` : undefined}
    />
  </div>
);

export const validateDateFormat = (values) => values && getDateFormat(values, dateFormatMMDDYYYY);

/**
* Return Fasat Date Picker Input Component
* @returns Component
*/
const generateFasatDateTimePicker = ({ isFieldDisable, values, showRequired, t, onChangeFasatDate, onBlurFasatDate, dataTestId, operatorKey }) => (
  <FasatDatePicker
    key={operatorKey}
    dateValue={values}
    dateFormat={dateFormatMMDDYYYY}
    disabled={isFieldDisable}
    errors={showRequired}
    textBoxAriaLabel={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
    onChange={onChangeFasatDate}
    onBlur={onBlurFasatDate}
    data-testid={dataTestId}
    hideMuxLabel
  />
);

/**
* Return Fasat Date Picker Range Input Component
* @returns Component
*/
const generateFasatDateTimePickerRange = ({ isFieldDisable, values, showRequired, t, uuid, onChangeFasatDateTime, onChangeFasatDateTimeTo, onBlurFasatDateTime, onBlurFasatDateTimeTo, dataTestId, operatorKey }) => (
  <div className="dateRange">
    <FasatDatePicker
      key={`${operatorKey}_from`}
      id={`${uuid}_to`}
      dateValue={values !== null ? values[0] : values}
      dateFormat={dateFormatMMDDYYYY}
      disabled={isFieldDisable}
      errors={showRequired}
      onChange={onChangeFasatDateTime}
      onBlur={onBlurFasatDateTime}
      textBoxAriaLabel={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
      data-testid={dataTestId ? `${dataTestId}-from` : undefined}
      hideMuxLabel
    />
    <span className="hyphen">-</span>
    <FasatDatePicker
      key={`${operatorKey}_to`}
      id={`${uuid}_from`}
      dateValue={values !== null ? values[1] : values}
      dateFormat={dateFormatMMDDYYYY}
      disabled={isFieldDisable}
      errors={showRequired}
      onChange={onChangeFasatDateTimeTo}
      onBlur={onBlurFasatDateTimeTo}
      textBoxAriaLabel={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
      data-testid={dataTestId ? `${dataTestId}-to` : undefined}
      hideMuxLabel
    />
  </div>
);

/**
  * Return Fasat Dropdown Input Component
  * @returns Component
  */
const generateDropdownComponent = (
  getDropdownOptions,
  _setValues,
  { isFieldDisable, uuid, inputConfiguration, values, t, operator, showRequired, onChangeHandleDropdown, dataTestId },
) => (
  <FasatDropdown
    dropdownItems={getDropdownOptions()}
    disabled={isFieldDisable}
    onHandleChange={onChangeHandleDropdown}
    id={uuid}
    isMultiselect={inputConfiguration.isMultiselect}
    value={values}
    placeholder={t(staticCommonLabelKeys.COMMON_PLACEHOLDER_SELECT)}
    filterCriteria
    operator={operator}
    errors={showRequired}
    ariaLabel={t(staticCommonLabelKeys.FILTER_LABEL_VALUE)}
    searchEnabled={inputConfiguration.isSearchable}
    searchPlaceholderText={t(staticCommonLabelKeys.COMMON_BUTTON_SEARCH)}
    data-testid={dataTestId}
  />
);

const validateInputComponent = (inputConfiguration) => (
  inputConfiguration.isInputComponent && (!inputConfiguration.isMultiselect
    && !inputConfiguration.isBetween)
);

const validateActionAbleCOmponent = (inputConfiguration) => (
  inputConfiguration.isInputComponent && (inputConfiguration.isMultiselect
    && !inputConfiguration.isBetween)
);

const validateMultiSelect = (inputConfiguration) => (
  inputConfiguration.isInputComponent && (!inputConfiguration.isMultiselect
    && inputConfiguration.isBetween)
);

const validateDateTimePicker = (inputConfiguration) => (
  inputConfiguration.isDatePickerComponent && !inputConfiguration.isBetween
);

const validateDateTimeRange = (inputConfiguration) => (
  inputConfiguration.isDatePickerComponent && inputConfiguration.isBetween
);

const getRequiredMsg = ({ uuid, t }) => [{ id: uuid,
  msg: t(staticCommonLabelKeys.REQFLD),
  isError: true }];

const getInvalidDateMsg = ({ uuid, t }) => [{ id: uuid,
  msg: t(staticCommonLabelKeys.COMMON_DATE_INVALID_FORMAT),
  isError: true }];

const setOnValueChange = ({ columnValue, operator, values, uuid, onValueChange }) => {
  const onValueChangeEvent = { key: columnValue.value,
    operator: operator.operator,
    type: columnValue.type,
    values,
    uuid,
  };
  onValueChange(onValueChangeEvent);
};

const handleOperatoreChange = (operator, setIsFieldDisable, setValues) => {
  if (operator) {
    const oprChk1 = operator.id === OPT_IS_NULL_ID || operator.id === OPT_IS_NOT_NULL_ID;
    const oprChk2 = operator.id === OPT_IS_EMPTY_ID || operator.id === OPT_IS_NOT_EMPTY_ID;
    if (oprChk1 || oprChk2) {
      setIsFieldDisable(true);
    } else {
      setIsFieldDisable(false);
    }
  } else {
    setIsFieldDisable(true);
  }
  setValues(null);
};

// Helper: Handle required field validation
const useRequiredValidation = (showInputRequired, values, uuid, t) => {
  const [showRequired, setShowRequired] = useState([]);

  useEffect(() => {
    if (showInputRequired) {
      if (values && showRequired.length > 0) {
        setShowRequired([]);
      }
      if (!values && showRequired.length === 0) {
        setShowRequired(getRequiredMsg({ uuid, t }));
      }
    }
  }, [values, showInputRequired]);

  return [showRequired, setShowRequired];
};

// Helper: Handle operator changes
const useOperatorEffect = (operator, setIsFieldDisable, setValues) => {
  useEffect(() => {
    handleOperatoreChange(operator, setIsFieldDisable, setValues);
  }, [operator]);
};

// Helper: Handle value changes
const useValueChangeEffect = (columnValue, operator, values, uuid, onValueChange) => {
  useEffect(() => {
    if (columnValue && columnValue.value && operator) {
      setOnValueChange({ columnValue, operator, values, uuid, onValueChange });
    }
  }, [values]);
};

// Helper: Get dropdown options
const getDropdownOptionsForColumn = (lookupData, columnValue) => {
  if (lookupData && columnValue) {
    const levelItems = lookupData
      .filter((lookUpItem) => lookUpItem.key === columnValue.lookupKey);
    if (levelItems && levelItems.length > 0) {
      return levelItems[0].values;
    }
  }
  return [];
};

// Helper: Render date error component
const renderDateError = (invalidDateError, uuid) => (
  invalidDateError.length > 0
    ? <div className="fc-date-error"><FasatFormErrors name={uuid} errors={invalidDateError} /></div>
    : null
);

// Helper: Get combined errors for date fields
const getDateFieldErrors = (showRequired, invalidDateError) => (
  showRequired.length > 0 ? showRequired : invalidDateError
);

// Helper: Render filter input content based on configuration
const renderFilterInputContent = ({
  inputConfiguration, isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId,
  setRangeValueFrom, setRangeValueTo, setValues, invalidDateError, validateAndSetDateError,
  uuid, getDropdownOptions, operator,
}) => (
  <>
    {validateInputComponent(inputConfiguration)
      && generateInputComponent(isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId)}
    {validateActionAbleCOmponent(inputConfiguration)
      && grtFasatActionTxtInp(isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId)}
    {validateMultiSelect(inputConfiguration) && generateFasatTextInputRange({
      isFieldDisable,
      type,
      t,
      values,
      dataTestId,
      handleChangeRangeFrom: (event) => setRangeValueFrom(event.target.value),
      handleChangeTo: (event) => setRangeValueTo(event.target.value),
    })}
    {validateDateTimePicker(inputConfiguration) && generateFasatDateTimePicker({
      isFieldDisable,
      values,
      showRequired: getDateFieldErrors(showRequired, invalidDateError),
      t,
      dataTestId,
      operatorKey: operator?.operator,
      onChangeFasatDate: (newValue) => { setValues(newValue); },
      onBlurFasatDate: (_event, dateVal) => { validateAndSetDateError(dateVal); },
    })}
    {validateDateTimePicker(inputConfiguration) && renderDateError(invalidDateError, uuid)}
    {validateDateTimeRange(inputConfiguration) && generateFasatDateTimePickerRange({
      isFieldDisable,
      values,
      showRequired: getDateFieldErrors(showRequired, invalidDateError),
      t,
      uuid,
      dataTestId,
      operatorKey: operator?.operator,
      onChangeFasatDateTime: (newValue) => { setRangeValueFrom(newValue); },
      onChangeFasatDateTimeTo: (newValue) => { setRangeValueTo(newValue); },
      onBlurFasatDateTime: (_event, dateVal) => { validateAndSetDateError(dateVal); },
      onBlurFasatDateTimeTo: (_event, dateVal) => { validateAndSetDateError(dateVal); },
    })}
    {validateDateTimeRange(inputConfiguration) && renderDateError(invalidDateError, uuid)}
    {inputConfiguration.isDropdownComponent && generateDropdownComponent(
      getDropdownOptions,
      setValues,
      { isFieldDisable, uuid, inputConfiguration, values, t, operator, showRequired, dataTestId, onChangeHandleDropdown: (val) => setValues(val) },
    )}
  </>
);

// Helper: Handle input value changes
const handleInputValueChange = (value, inputConfiguration, setValues) => {
  const changeVal = inputConfiguration.isMultiselect ? value : value.target.value;
  setValues(changeVal);
};

// Helper: Create range value setter
const createRangeValueSetter = (setValues, values, isFrom) => (value) => {
  const formatObject = isFrom
    ? [value, values ? values[1] : null]
    : [values ? values[0] : null, value];
  setValues(formatObject);
};

function FasatFilterInput({ columnValue, operator, onValueChange, lookupData, type, uuid, inputConfiguration,
  preSelectedIndex, customFilterCreteriaElement, showInputRequired, dataTestId }) {
  const [values, setValues] = useState(null);
  const locale = getCurrentLocale();
  const [t] = useTranslation();
  const [isFieldDisable, setIsFieldDisable] = useState(true);
  const [invalidDateError, setInvalidDateError] = useState([]);

  // Use extracted hooks
  const [showRequired, setShowRequired] = useRequiredValidation(showInputRequired, values, uuid, t);
  useOperatorEffect(operator, setIsFieldDisable, setValues);
  useValueChangeEffect(columnValue, operator, values, uuid, onValueChange);

  // Clear date error when operator changes
  useEffect(() => {
    setInvalidDateError([]);
  }, [operator]);

  // Validate date format and set error if invalid
  const validateAndSetDateError = (dateValue) => {
    const hasInvalidDate = dateValue && !dateFieldValidator(dateValue);
    setInvalidDateError(hasInvalidDate ? getInvalidDateMsg({ uuid, t }) : []);
  };

  // Handle locale changes for required field
  useEffect(() => {
    const shouldShowRequired = showInputRequired && !values;
    setShowRequired(shouldShowRequired ? getRequiredMsg({ uuid, t }) : []);
  }, [showInputRequired, locale]);

  // Set value for pre-populated values
  useEffect(() => {
    const hasPreSelectedValue = preSelectedIndex > -1
      && customFilterCreteriaElement[preSelectedIndex]?.values && operator;
    if (hasPreSelectedValue) {
      setValues(customFilterCreteriaElement[preSelectedIndex].values);
    }
  }, [operator]);

  const getDropdownOptions = () => getDropdownOptionsForColumn(lookupData, columnValue);
  const changeInputValue = (value) => handleInputValueChange(value, inputConfiguration, setValues);
  const setRangeValueFrom = createRangeValueSetter(setValues, values, true);
  const setRangeValueTo = createRangeValueSetter(setValues, values, false);

  return renderFilterInputContent({
    inputConfiguration, isFieldDisable, changeInputValue, type, values, showRequired, t, dataTestId,
    setRangeValueFrom, setRangeValueTo, setValues, invalidDateError, validateAndSetDateError,
    uuid, getDropdownOptions, operator,
  });
}

FasatFilterInput.propTypes = {
  columnValue: PropTypes.shape({
    value: PropTypes.string,
    type: PropTypes.string,
    lookupKey: PropTypes.string,
  }),
  operator: PropTypes.shape(),
  onValueChange: PropTypes.func,
  lookupData: PropTypes.arrayOf(PropTypes.shape()),
  type: PropTypes.string,
  uuid: PropTypes.string,
  inputConfiguration: PropTypes.shape({
    isInputComponent: PropTypes.bool,
    isMultiselect: PropTypes.bool,
    isSearchable: PropTypes.bool,
    isBetween: PropTypes.bool,
    isDatePickerComponent: PropTypes.bool,
    isDropdownComponent: PropTypes.bool,
    autoPopulate: PropTypes.bool,
  }),
  customFilterCreteriaElement: PropTypes.arrayOf(PropTypes.shape()),
  preSelectedIndex: PropTypes.number,
  showInputRequired: PropTypes.bool,
  dataTestId: PropTypes.string,
};

FasatFilterInput.defaultProps = {
  columnValue: {
    value: null,
    type: null,
    lookupKey: null,
  },
  operator: null,
  onValueChange: () => undefined,
  lookupData: null,
  type: 'text',
  uuid: null,
  customFilterCreteriaElement: null,
  preSelectedIndex: -1,
  inputConfiguration: {
    isInputComponent: true,
    isMultiselect: false,
    isBetween: false,
    isDatePickerComponent: false,
    isDropdownComponent: false,
    autoPopulate: false,
    isSearchable: false,
  },
  showInputRequired: false,
  dataTestId: undefined,
};

export default FasatFilterInput;
